import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useChatStore } from '@/stores/chat-store';
import { useWebSocketStore } from '@/stores/websocket-store';
import { ConversationList } from './components/conversation-list';
import { ChatPanel } from './components/chat-panel';
import { EmptyState } from './components/empty-state';
import { NewConversationDialog } from './components/new-conversation-dialog';
import { Menu, Plus } from 'lucide-react';
import { Button } from '@caruso/ui';
import { cn } from '@caruso/ui';
import { Conversation } from '@/services/chat.service';

export function WebChatPage() {
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isNewConversationOpen, setIsNewConversationOpen] = useState(false);

  const {
    selectedConversation,
    fetchConversations,
    fetchStats,
    selectConversation
  } = useChatStore();

  const {
    connect,
    disconnect,
    isConnected,
    joinConversation,
    leaveConversation
  } = useWebSocketStore();

  // Conectar WebSocket ao montar
  useEffect(() => {
    connect();
    fetchConversations();
    fetchStats();

    // Atualizar stats periodicamente
    const statsInterval = setInterval(() => {
      fetchStats();
    }, 30000);

    return () => {
      disconnect();
      clearInterval(statsInterval);
    };
  }, [connect, disconnect, fetchConversations, fetchStats]);

  // Gerenciar rooms do WebSocket quando selecionar conversa
  useEffect(() => {
    if (selectedConversation && isConnected) {
      joinConversation(selectedConversation.id);

      return () => {
        leaveConversation(selectedConversation.id);
      };
    }
  }, [selectedConversation, isConnected, joinConversation, leaveConversation]);

  // Fechar sidebar mobile quando selecionar conversa
  useEffect(() => {
    if (selectedConversation) {
      setIsMobileSidebarOpen(false);
    }
  }, [selectedConversation]);

  // Callback quando nova conversa é criada
  const handleConversationCreated = (conversation: Conversation) => {
    fetchConversations();
    fetchStats();
    selectConversation(conversation);
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex bg-brand-ink rounded-xl overflow-hidden border border-brand-gold/10">
      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {isMobileSidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Conversation List Sidebar */}
      <aside
        className={cn(
          'w-full md:w-80 lg:w-96 flex-shrink-0 bg-brand-ink border-r border-brand-gold/10',
          'md:relative md:translate-x-0',
          'fixed inset-y-0 left-0 z-50 transition-transform duration-300',
          isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        )}
      >
        <ConversationList onClose={() => setIsMobileSidebarOpen(false)} />
      </aside>

      {/* Chat Panel */}
      <main className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center justify-between p-4 border-b border-brand-gold/10">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsMobileSidebarOpen(true)}
              className="text-brand-gold"
            >
              <Menu className="w-5 h-5" />
            </Button>
            <span className="text-brand-ivory font-medium">
              {selectedConversation ? selectedConversation.client.name : 'Conversas'}
            </span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setIsNewConversationOpen(true)}
            className="text-brand-gold hover:bg-brand-gold/10"
          >
            <Plus className="w-5 h-5" />
          </Button>
        </div>

        {selectedConversation ? (
          <ChatPanel />
        ) : (
          <EmptyState onNewConversation={() => setIsNewConversationOpen(true)} />
        )}
      </main>

      {/* New Conversation Dialog */}
      <NewConversationDialog
        isOpen={isNewConversationOpen}
        onClose={() => setIsNewConversationOpen(false)}
        onConversationCreated={handleConversationCreated}
      />
    </div>
  );
}
